import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import {
  AlertSuppressionSetting,
  useAbsolutePriceDropDialog,
  useManualPriceDisableAutoPricingDialog,
  useRelativeFloorPriceDropDialog,
  useRelativePriceDropDialog,
  useSuspiciousPriceChangeDialog,
} from 'src/dialogs/AlertWithSuppressionDialog';
import { useManualPriceAdjustFloorOrCeilingDialog } from 'src/dialogs/AlertWithSuppressionDialog/useManualPriceAdjustFloorOrCeilingDialog';
import {
  addRequestedEventIdsAtom,
  dialogSuppressionByEventIdAtom,
} from 'src/hooks/api/useEventConfirmationDialogSuppressions.atoms';
import { ConfirmationDialogType } from 'src/WebApiController';
import { SellerEventConfirmationDialogSuppression } from 'src/WebApiController/index';

const backfillSellerEventSuppression = (
  dialogProps: {
    lastTimeStamp: Date | undefined | null;
    bodyText: JSX.Element;
    key: keyof AlertSuppressionSetting;
    onOkay?: (() => void) | undefined;
    onCancel?: (() => void) | undefined;
  },
  suppression?: SellerEventConfirmationDialogSuppression
) => {
  const lastTimeStamp = suppression?.endDate
    ? new Date(suppression.endDate)
    : dialogProps.lastTimeStamp;

  return {
    ...dialogProps,
    lastTimeStamp,
  };
};

export const useSellerEventDialogSuppressions = (
  eventIds: string[],
  hasSellerEventAlertMutes: boolean
) => {
  const addSuppressionsEventIds = useSetAtom(addRequestedEventIdsAtom);
  const getSuppressionsByEventId = useAtomValue(dialogSuppressionByEventIdAtom);

  useEffect(() => {
    if (eventIds && eventIds.length > 0) {
      addSuppressionsEventIds({
        eventIds: eventIds,
        enabled: hasSellerEventAlertMutes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addSuppressionsEventIds,
    hasSellerEventAlertMutes,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(eventIds?.toSorted()),
  ]);

  // --- Dialog timestamp hooks (1 instance, not N per row) ---
  // onOkay/onCancel are no-ops here; per-row callbacks are injected at call time
  const suspiciousPriceChangeHookProps = useSuspiciousPriceChangeDialog(
    () => {},
    () => {}
  );
  const absolutePriceDropProps = useAbsolutePriceDropDialog(
    () => {},
    () => {}
  );
  const relativePriceDropProps = useRelativePriceDropDialog(
    () => {},
    () => {}
  );
  const relativeFloorPriceDropProps = useRelativeFloorPriceDropDialog(
    () => {},
    () => {}
  );
  const manualPriceDisableAutoPricingProps =
    useManualPriceDisableAutoPricingDialog(() => {});
  const manualPriceAdjustFloorCeilingProps =
    useManualPriceAdjustFloorOrCeilingDialog(
      null,
      null,
      null,
      () => {},
      () => {}
    );

  const getDialogProps = useCallback(
    (eventId: string) => {
      const suppression = getSuppressionsByEventId(eventId);

      return {
        suspiciousPriceChangeHookProps: backfillSellerEventSuppression(
          suspiciousPriceChangeHookProps,
          suppression[ConfirmationDialogType.SuspiciousPriceChange]
        ),
        absolutePriceDropProps: backfillSellerEventSuppression(
          absolutePriceDropProps,
          suppression[ConfirmationDialogType.AbsolutePriceDrop]
        ),
        relativePriceDropProps: backfillSellerEventSuppression(
          relativePriceDropProps,
          suppression[ConfirmationDialogType.RelativePriceDrop]
        ),
        relativeFloorPriceDropProps: backfillSellerEventSuppression(
          relativeFloorPriceDropProps,
          suppression[ConfirmationDialogType.RelativeFloorPriceDrop]
        ),
        manualPriceDisableAutoPricingProps: backfillSellerEventSuppression(
          manualPriceDisableAutoPricingProps,
          suppression[ConfirmationDialogType.AutoPricingDisable]
        ),
        manualPriceAdjustFloorCeilingProps: backfillSellerEventSuppression(
          manualPriceAdjustFloorCeilingProps,
          suppression[ConfirmationDialogType.FloorCeilingViolation]
        ),
      };
    },
    [
      absolutePriceDropProps,
      getSuppressionsByEventId,
      manualPriceAdjustFloorCeilingProps,
      manualPriceDisableAutoPricingProps,
      relativeFloorPriceDropProps,
      relativePriceDropProps,
      suspiciousPriceChangeHookProps,
    ]
  );

  return {
    getDialogProps,
  };
};
